/**
 * Affirmation — the gentle "well done" header inside RoundEnd.
 *
 * Generates a kind, audience-appropriate message based on accuracy and total.
 * No streak math, no shaming language. Even a $0 round gets a real
 * affirmation — these players showed up.
 */

import './Affirmation.css';

export interface AffirmationProps {
  score: number;
  totalAnswered: number;
  totalCorrect: number;
}

export function Affirmation({
  score,
  totalAnswered,
  totalCorrect,
}: AffirmationProps): React.JSX.Element {
  const accuracy = totalAnswered > 0 ? totalCorrect / totalAnswered : 0;
  const { headline, blurb } = messageFor(score, accuracy, totalCorrect);

  return (
    <header className="tb-aff">
      <p className="tb-aff__eyebrow">Round complete</p>
      <h2 className="tb-aff__headline">{headline}</h2>
      <p className="tb-aff__blurb">{blurb}</p>
    </header>
  );
}

function messageFor(
  score: number,
  accuracy: number,
  correct: number,
): { headline: string; blurb: string } {
  // Six tiers. None punish. All sincere.
  if (correct === 0) {
    return {
      headline: 'A round well played.',
      blurb:
        'A few tricky categories tonight. Show up tomorrow — these things have a way of clicking.',
    };
  }
  if (score >= 12000) {
    return {
      headline: 'A magnificent run.',
      blurb: 'You moved through that board like you wrote half the clues. Take the win.',
    };
  }
  if (score >= 8000) {
    return {
      headline: 'A strong board.',
      blurb: 'A confident, sharp performance. You knew what you knew and answered like it.',
    };
  }
  if (score >= 4000 || accuracy >= 0.5) {
    return {
      headline: 'Nicely done.',
      blurb: 'Solid round. A few right, a few that drifted — exactly how it should feel.',
    };
  }
  return {
    headline: 'A good board to start.',
    blurb: 'Every round teaches you something. You picked up a handful — come back tomorrow.',
  };
}
